import React, { useEffect, useState } from "react";
import {
  isPushSupported,
  getPushStatus,
  subscribeToPush,
  unsubscribeFromPush,
} from "../utils/push";

const BellIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
    />
  </svg>
);

const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

// "Notify me" toggle for web push. `variant`: "header" (compact) | "hero" (full).
const NotifyButton = ({ variant = "header" }) => {
  const [status, setStatus] = useState("loading"); // loading | unsupported | denied | unsubscribed | subscribed
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    getPushStatus().then((s) => mounted && setStatus(s));
    return () => {
      mounted = false;
    };
  }, []);

  if (!isPushSupported() || status === "unsupported" || status === "loading") {
    return null;
  }

  const subscribed = status === "subscribed";
  const denied = status === "denied";

  const handleClick = async () => {
    if (busy || denied) return;
    setBusy(true);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setStatus("unsubscribed");
      } else {
        await subscribeToPush();
        setStatus("subscribed");
      }
    } catch (err) {
      if (err.message === "denied") setStatus("denied");
    } finally {
      setBusy(false);
    }
  };

  const label = denied
    ? "Notifications blocked"
    : subscribed
    ? variant === "hero"
      ? "Subscribed to new episodes"
      : "Subscribed"
    : variant === "hero"
    ? "Notify me about new episodes"
    : "Notify me";

  const base =
    "inline-flex items-center gap-2 font-semibold transition-all duration-200 disabled:opacity-60";
  const styles =
    variant === "hero"
      ? denied
        ? `${base} px-5 py-3 rounded-lg bg-slate-100 border border-slate-200 text-slate-400 text-sm cursor-not-allowed`
        : subscribed
        ? `${base} px-5 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm`
        : `${base} px-5 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm`
      : denied
      ? `${base} px-3.5 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-400 text-xs cursor-not-allowed`
      : subscribed
      ? `${base} px-3.5 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs`
      : `${base} px-3.5 py-2 rounded-full border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600 text-xs`;

  return (
    <button
      onClick={handleClick}
      disabled={busy || denied}
      title={
        denied
          ? "You've blocked notifications for this site — enable them in your browser settings."
          : subscribed
          ? "Click to stop receiving new-episode notifications"
          : "Get a notification when a new episode is published"
      }
      className={styles}
    >
      {subscribed ? (
        <CheckIcon className={variant === "hero" ? "w-4 h-4" : "w-3.5 h-3.5"} />
      ) : (
        <BellIcon className={variant === "hero" ? "w-4 h-4" : "w-3.5 h-3.5"} />
      )}
      {busy ? "..." : label}
    </button>
  );
};

export default NotifyButton;
